// src/components/analysis/detail/TeamAnalysisDetail.js
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Rating,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import CodeIcon from '@mui/icons-material/Code';
import BrushIcon from '@mui/icons-material/Brush';
import StarIcon from '@mui/icons-material/Star';
import WorkIcon from '@mui/icons-material/Work';

// Helper function to format keys for display
const formatEvaluationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Custom list item component for better formatting
const EnhancedListItem = ({ children }) => (
  <ListItem 
    sx={{ 
      padding: 0,
      paddingY: 0.5,
      marginLeft: 0
    }}
  >
    <ListItemIcon sx={{ minWidth: 28 }}>
      <CheckCircleOutlineIcon color="primary" fontSize="small" />
    </ListItemIcon>
    <ListItemText 
      primary={
        <Typography variant="body2">
          {children}
        </Typography>
      } 
    />
  </ListItem>
);

// Section header component
const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" color="primary.main" fontWeight="bold">
      {title}
    </Typography>
  </Box>
);

const TeamMemberGrid = ({ title, members, icon }) => {
  if (!members || members.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Typography>
      <Grid container spacing={2}>
        {members.map((member, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%', 
                borderColor: 'primary.light',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 1
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {member.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.title}
                    </Typography>
                  </Box>
                </Box>
                
                {member.expertise && (
                  <Box sx={{ mt: 1, display: 'flex' }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '75px' }}>
                      Expertise:
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {member.expertise}
                    </Typography>
                  </Box>
                )}
                {member.background && (
                  <Box sx={{ mt: 0.5, display: 'flex' }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '75px' }}>
                      Background:
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {member.background}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const TeamAnalysisDetail = ({ data }) => {
  // Extract the evaluation and team data
  const evaluation = data.evaluation || {};
  const leadership = data.Leadership || [];
  const engineering = data.Engineering || [];
  const business = data["Business & Operations"] || [];
  const design = data.Design || [];
  const others = data["Team Members"] || [];

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <SectionHeader 
          icon={<PeopleIcon fontSize="small" />}
          title="Team Composition"
        />
      
        <Box sx={{ px: 1 }}>
          <TeamMemberGrid 
            title="Leadership Team" 
            members={leadership} 
            icon={<BusinessIcon fontSize="small" sx={{ color: 'primary.main' }} />} 
          />
          <TeamMemberGrid 
            title="Engineering" 
            members={engineering} 
            icon={<CodeIcon fontSize="small" sx={{ color: 'primary.main' }} />} 
          />
          <TeamMemberGrid 
            title="Design" 
            members={design} 
            icon={<BrushIcon fontSize="small" sx={{ color: 'primary.main' }} />} 
          />
          <TeamMemberGrid 
            title="Business & Operations" 
            members={business} 
            icon={<WorkIcon fontSize="small" sx={{ color: 'primary.main' }} />} 
          />
          <TeamMemberGrid 
            title="Other Team Members" 
            members={others} 
            icon={<PersonIcon fontSize="small" sx={{ color: 'primary.main' }} />} 
          />
        </Box>
      </Paper>
      
      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1 }} />
        Team Evaluation
      </Typography>
      
      <Grid container spacing={3}>
        {Object.entries(evaluation)
          .filter(([key]) => key !== 'overall_team_assessment')
          .map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderWidth: 1, 
                  borderColor: 'primary.light',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 1
                  }
                }}
              >
                <CardHeader 
                  title={formatEvaluationKey(key)} 
                  titleTypographyProps={{ fontWeight: 'bold', variant: 'subtitle1' }}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  }}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating 
                        value={parseFloat(value.score)} 
                        precision={0.5} 
                        readOnly 
                        emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1, 
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {value.score}/5
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2">
                    {value.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
      
      {evaluation.overall_team_assessment && (
        <Box sx={{ mt: 3 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 1,
              borderLeft: 4, 
              borderColor: 'primary.main'
            }}
          >
            <CardHeader 
              title="Overall Team Assessment" 
              titleTypographyProps={{ fontWeight: 'bold', variant: 'h6', color: 'primary.main' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Rating 
                    value={parseFloat(evaluation.overall_team_assessment.score)} 
                    precision={0.5} 
                    readOnly 
                    sx={{ color: 'primary.main' }}
                    emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    {evaluation.overall_team_assessment.score}/5
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography variant="body1">
                {evaluation.overall_team_assessment.summary}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default TeamAnalysisDetail;